// Editable homepage wording. Admins change it under Admin → Homepage Text;
// the backend stores only their overrides (site_content/homepage), so any
// field left alone — or cleared — shows the default below. The admin screen
// is generated from this list, so adding a field here makes it editable.
//
// In fields marked `accent`, words wrapped in *asterisks* are set in the
// orange italic, and an "&" is always accented (see AccentText).
//
// Admin-only field options:
//   group      heading the field is listed under in the admin
//   previewAs  who sees this text ('guest', 'pending', 'active', or a list of
//              them); the admin preview switches to that visitor when needed
//   tokens     {placeholders} the admin can insert, replaced on the storefront
//   needs      data the text only shows with ('featuredProducts' or 'events')

const TRADE_AUDIENCES = [
  'Retail stores & Western boutiques',
  'Interior designers & decorators',
  'Furniture stores & showrooms',
  'Online retailers',
  'Hotels, lodges & hospitality',
  'Event planners & staging companies',
  'Ranches & agricultural businesses',
];

export const HOMEPAGE_COPY_SECTIONS = [
  {
    id: 'announcement',
    title: 'Top announcement bar',
    summary: 'The thin dark strip at the very top of every page.',
    description: 'Its messages take turns every few seconds. The trade-show message is made automatically from Wholesale Events, and approved customers see “Welcome back” instead of the visitor message.',
    fields: [
      { key: 'announce.guest', label: 'Message for visitors', hint: 'Shown to anyone who isn’t signed in.', previewAs: 'guest', default: 'Wholesale pricing available with a trade account' },
      { key: 'announce.guestCta', label: 'Clickable word after that message', hint: 'Opens the trade-account sign-up form.', previewAs: 'guest', default: 'Apply' },
      { key: 'announce.pending', label: 'Message for customers waiting for approval', hint: 'Shown to signed-in customers until their trade account is approved.', previewAs: 'pending', default: 'Your trade application is under review' },
      { key: 'announce.faire', label: 'Faire message', hint: 'Everyone sees this one. Clicking it opens your Faire shop.', default: 'Prefer Faire? Order BS Trading wholesale there too' },
    ],
  },
  {
    id: 'hero',
    title: 'Main banner',
    summary: 'The big headline and photo at the top of the homepage.',
    description: 'These are the smaller pieces around the headline. The headline, paragraph and photo themselves are changed on the Homepage screen.',
    fields: [
      { key: 'hero.eyebrow', label: 'Small line above the headline', hint: 'Signed-in customers see “Welcome back, (their first name)” here instead.', previewAs: 'guest', default: 'Wholesale home décor · Burleson, Texas' },
      { key: 'hero.primaryCta', label: 'Main button', hint: 'Takes visitors to the full catalog.', default: 'Shop the collection' },
      { key: 'hero.applyCta', label: 'Second button', hint: 'Only visitors who aren’t signed in see it. It opens the trade-account sign-up form.', previewAs: 'guest', default: 'Apply for trade pricing' },
      { key: 'hero.badge', label: 'Note beside the collection counts', hint: 'Approved customers see “Trade account approved” here instead.', previewAs: ['guest', 'pending'], default: 'Wholesale pricing for approved accounts' },
      { key: 'hero.featuringLabel', label: '“Now featuring” card — small label', hint: 'The card on the banner photo that shows your featured products one at a time.', needs: 'featuredProducts', default: 'Now featuring' },
      { key: 'hero.featuringCta', label: '“Now featuring” card — link text', needs: 'featuredProducts', default: 'View piece' },
    ],
  },
  {
    id: 'collections',
    title: 'Shop by collection',
    summary: 'The photo tiles of your featured collections.',
    description: 'Which collections appear, and in what order, is set on the Homepage screen.',
    fields: [
      { key: 'collections.eyebrow', label: 'Small label above the heading', default: 'Shop by collection' },
      { key: 'collections.title', label: 'Heading', default: 'Built on hides. *Styled* for home.', accent: true },
      {
        key: 'collections.body',
        label: 'Paragraph',
        multiline: true,
        default: '{collections} collections and {categories} categories, from natural cowhides and Tibetan lamb to onyx, taxidermy and closeout finds — stocked for boutiques, designers and showrooms.',
        hint: 'The {collections} and {categories} tags turn into your live counts, like “14 collections”.',
        tokens: [
          { name: 'collections', label: 'Number of collections' },
          { name: 'categories', label: 'Number of categories' },
        ],
      },
      { key: 'collections.link', label: 'Link to the full catalog', default: 'Browse the full catalog' },
    ],
  },
  {
    id: 'trade',
    title: 'For the trade',
    summary: 'The dark wholesale section in the middle of the page.',
    description: 'The bottom of this section depends on who’s looking: visitors see sign-up buttons, while customers waiting for approval and approved customers each see their own message.',
    warning: 'Never mention pricing tiers or tier names here — customers must not be able to tell which tier they’re on. “Wholesale pricing” is fine.',
    fields: [
      { key: 'trade.eyebrow', group: 'Heading and paragraph', label: 'Small label above the heading', default: 'For the trade' },
      { key: 'trade.title', group: 'Heading and paragraph', label: 'Heading', default: 'Wholesale pricing for boutiques, designers & showrooms.', accent: true },
      { key: 'trade.body', group: 'Heading and paragraph', label: 'Paragraph', multiline: true, default: 'Open a trade account to unlock wholesale pricing across the full catalog, backed by our team in Burleson, Texas.' },
      { key: 'trade.benefit1Title', group: 'Benefit 1', label: 'Title', default: 'Wholesale pricing' },
      { key: 'trade.benefit1Text', group: 'Benefit 1', label: 'Description', multiline: true, default: 'Competitive pricing on bulk orders across the catalog once your account is approved.' },
      { key: 'trade.benefit2Title', group: 'Benefit 2', label: 'Title', default: 'Dedicated account support' },
      { key: 'trade.benefit2Text', group: 'Benefit 2', label: 'Description', multiline: true, default: 'Experienced staff to help with product selection, quantities and custom orders.' },
      { key: 'trade.benefit3Title', group: 'Benefit 3', label: 'Title', default: 'Priority on new inventory' },
      { key: 'trade.benefit3Text', group: 'Benefit 3', label: 'Description', multiline: true, default: 'Priority access when new inventory arrives.' },
      { key: 'trade.benefit4Title', group: 'Benefit 4', label: 'Title', default: 'Flexible terms' },
      { key: 'trade.benefit4Text', group: 'Benefit 4', label: 'Description', multiline: true, default: 'Payment terms and custom ordering for qualified trade accounts.' },
      { key: 'trade.applyCta', group: 'Buttons for visitors', label: 'Sign-up button', hint: 'Opens the trade-account sign-up form.', previewAs: 'guest', default: 'Apply for a trade account' },
      { key: 'trade.signInCta', group: 'Buttons for visitors', label: 'Sign-in button', previewAs: 'guest', default: 'Sign in' },
      { key: 'trade.learnMore', group: 'Buttons for visitors', label: 'Link to the Wholesale page', previewAs: 'guest', default: 'How it works' },
      { key: 'trade.pendingTitle', group: 'Customers waiting for approval see', label: 'Heading', previewAs: 'pending', default: 'Your application is in review.' },
      { key: 'trade.pendingText', group: 'Customers waiting for approval see', label: 'Text', multiline: true, previewAs: 'pending', default: 'Our team reviews every trade application. Questions in the meantime? Call 1-800-554-0910.' },
      { key: 'trade.pendingCta', group: 'Customers waiting for approval see', label: 'Button', hint: 'Goes to the Contact page.', previewAs: 'pending', default: 'Contact the team' },
      { key: 'trade.approvedTitle', group: 'Approved customers see', label: 'Heading', previewAs: 'active', default: 'Your trade account is approved.' },
      { key: 'trade.approvedCta', group: 'Approved customers see', label: 'Button', hint: 'Goes to the full catalog.', previewAs: 'active', default: 'Shop the full catalog' },
      { key: 'trade.approvedLink', group: 'Approved customers see', label: 'Link', hint: 'Goes to the customer’s account page.', previewAs: 'active', default: 'Reorder from your account' },
      { key: 'trade.audienceLabel', group: 'Who we work with', label: 'Label above the list', default: 'Who we work with' },
      { key: 'trade.audiences', group: 'Who we work with', label: 'The list', multiline: true, rows: 8, default: TRADE_AUDIENCES.join('\n'), hint: 'Put each one on its own line. They’re numbered automatically.' },
      { key: 'trade.faireKicker', group: 'Faire card', label: 'Small line', default: 'Prefer a marketplace?' },
      { key: 'trade.faireLabel', group: 'Faire card', label: 'Link text', hint: 'Opens your Faire shop.', default: 'Shop BS Trading on Faire' },
    ],
  },
  {
    id: 'findUs',
    title: 'Where to find us',
    summary: 'Your showroom and upcoming trade shows.',
    description: 'The address, phone number and hours are fixed and match the footer. Trade-show cards come from Wholesale Events (the next two upcoming shows).',
    fields: [
      { key: 'findUs.eyebrow', label: 'Small label above the heading', default: 'Where to find us' },
      { key: 'findUs.title', label: 'Heading', default: 'See it *in person.*', accent: true },
      { key: 'findUs.body', label: 'Paragraph', multiline: true, default: 'Hides are one of a kind — color and pattern vary piece to piece. Visit us in Burleson or give us a call, and we’ll help you choose.' },
      { key: 'findUs.showroomLabel', label: 'Showroom card — small label', default: 'Visit us' },
      { key: 'findUs.showroomTitle', label: 'Showroom card — heading', default: 'Burleson, Texas' },
      { key: 'findUs.marketLabel', label: 'Trade-show card — small label', hint: 'Shown on each upcoming trade show from Wholesale Events.', needs: 'events', default: 'Meet us at market' },
    ],
  },
  {
    id: 'catalog',
    title: 'Everything we carry',
    summary: 'The list of every collection at the bottom of the page.',
    description: 'The list itself updates automatically from Categories.',
    fields: [
      { key: 'catalog.eyebrow', label: 'Small label above the heading', default: 'The full catalog' },
      { key: 'catalog.title', label: 'Heading', default: 'Everything *we carry.*', accent: true },
      { key: 'catalog.body', label: 'Paragraph', multiline: true, default: 'Looking for something specific? Search the catalog by name, style or SKU.' },
      { key: 'catalog.searchLabel', label: 'Search button text', default: 'Search the catalog' },
    ],
  },
];

export const HOMEPAGE_COPY_DEFAULTS = Object.fromEntries(
  HOMEPAGE_COPY_SECTIONS.flatMap((section) => section.fields.map((field) => [field.key, field.default])),
);

// Defaults with the admin's non-blank overrides applied; unknown keys ignored.
export const resolveHomepageCopy = (overrides) => {
  const copy = { ...HOMEPAGE_COPY_DEFAULTS };
  Object.entries(overrides || {}).forEach(([key, value]) => {
    if (key in copy && typeof value === 'string' && value.trim()) copy[key] = value;
  });
  return copy;
};

// "{collections} collections" → "14 collections". Unknown tokens stay as typed.
export const fillCopy = (text, values) =>
  text.replace(/\{(\w+)\}/g, (token, name) => (name in values ? String(values[name]) : token));

export const copyHasTokens = (text, names) =>
  names.some((name) => text.includes(`{${name}}`));

export const copyLines = (text) =>
  text.split('\n').map((line) => line.trim()).filter(Boolean);
